'use client';

import React, { useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { PhotoIcon } from '@heroicons/react/24/outline';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import { classnames } from '@/utils/classnames/classnames';
import { ArticleCardProps } from './types';

const isExternal = (href?: string) => !!href && /^(https?:|mailto:|tel:)/i.test(href);

const ArticleCard = ({
  image,
  category,
  readTime,
  title,
  description,
  linkLabel = 'Learn more',
  link,
  highlightCard = false,
  priority = false,
}: ArticleCardProps) => {
  const href = link.href || '#';
  const external = isExternal(href) || !!link.openInNewTab;
  const [hasImageError, setHasImageError] = useState(false);

  // EWM-760 — reset the error flag when the image source changes (a fresh
  // URL deserves a fresh chance to load), adjusted during render instead of
  // a cascading `useEffect` — same trigger (`image.src` identity change),
  // one render sooner.
  const [prevSrc, setPrevSrc] = useState(image.src);
  if (prevSrc !== image.src) {
    setPrevSrc(image.src);
    setHasImageError(false);
  }

  const wrapperClassName =
    'focus-visible:outline-primary block h-full rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2';

  const content = (
    <article className="hover:shadow-300 grid h-full grid-rows-[auto_1fr] overflow-hidden rounded-lg border border-gray-200 bg-white transition-shadow">
      <div className={classnames('relative aspect-[16/9]', highlightCard ? 'bg-yellow-100 p-6' : 'bg-neutral-200')}>
        {hasImageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <PhotoIcon className="size-12 text-gray-300" aria-hidden />
          </div>
        ) : (
          <Image
            {...image}
            fill
            sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
            style={{ objectFit: highlightCard ? 'contain' : 'cover' }}
            alt={image.alt || image.media?.alt || title}
            priority={priority}
            onError={() => setHasImageError(true)}
          />
        )}
      </div>

      <div className="grid grid-rows-[auto_auto_1fr_auto] gap-2 p-5">
        <div className="text-11 text-primary flex items-center gap-2 font-semibold tracking-wide uppercase">
          <span>{category}</span>
          {readTime && (
            <>
              <span aria-hidden className="text-gray-400">
                •
              </span>
              <span className="text-gray-500">{readTime}</span>
            </>
          )}
        </div>

        <h3 className="text-16 lg:text-18 font-bold text-gray-800">{title}</h3>

        <p className="text-14 line-clamp-3 leading-normal text-gray-600">{description}</p>

        <span className="text-12 text-primary mt-2 flex items-center gap-1 font-semibold tracking-wide uppercase">
          {linkLabel}
          <ArrowRightIcon aria-hidden className="size-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </article>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={`group ${wrapperClassName}`}>
        {content}
      </a>
    );
  }

  return (
    <Link href={href} block underlineOnHover={false} className={`group ${wrapperClassName}`}>
      {content}
    </Link>
  );
};

export default ArticleCard;
